import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { stopImpersonation } from '@adornis/users/client/impersonation.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html, nothing } from 'lit';

/**
 * Who you are signed in as, and the two ways out of it.
 *
 * Both places that frame the app — the generate page header and the admin
 * sidebar — need the same pair, so they share one component rather than two
 * copies that drift. `layout` is the only difference: a row of ghost buttons in a
 * header, a stacked block with the account it belongs to in the sidebar footer.
 *
 * "Stop impersonation" is rendered off `isImpersonating` rather than off
 * `impersonatingUser()`: the latter is a subscription to the impersonating
 * account and a publish filter can legitimately keep it empty, while the flag
 * always reflects the session. Someone who is not impersonating never sees it.
 */
export const SessionActions = stateful(({ layout }: { layout: 'row' | 'column' }) => {
  const user = useObservable(() => currentUser(), []);
  const impersonating = useObservable(() => isImpersonating, []) ?? false;
  const account = user?.email ?? user?.username ?? '';

  const handleStopImpersonation = () =>
    guardDialog(() => stopImpersonation(), {
      loadingText: 'Returning to your own account…',
      successText: 'Impersonation stopped.',
      errorMessagePrefix: 'Could not stop impersonating',
      showLoaderModal: false,
    });

  /**
   * Confirmed, unlike stopping impersonation. The generate page holds an unsaved
   * form — picked templates and uploaded documents — and a stray click on a
   * header button would throw it away with no way back.
   */
  const handleLogout = () =>
    guardDialog(
      async () => {
        await logout();
        /**
         * The authentication guard swaps the whole app for the login form without
         * a page load, so the route survives the sign-out. Without this the next
         * person to log in on this tab lands wherever the previous one left off —
         * `/admin`, quite possibly forbidden for them.
         */
        goTo('/');
      },
      {
        hasToConfirm: true,
        confirmText: 'Log out of SARAA?',
        acceptText: 'Log out',
        declineText: 'Stay signed in',
        loadingText: 'Signing you out…',
        successText: 'Signed out.',
        errorMessagePrefix: 'Could not log out',
        showLoaderModal: false,
      },
    );

  const stopImpersonationButton = (classes: string) => html`
    <button type="button" class=${classes} @click=${() => void handleStopImpersonation()}>
      <span class="icon-[material-symbols--person-off-outline] text-base"></span>
      Stop impersonation
    </button>
  `;

  const logoutButton = (classes: string) => html`
    <button type="button" class=${classes} @click=${() => void handleLogout()}>
      <span class="icon-[material-symbols--logout] text-base"></span>
      Log out
    </button>
  `;

  if (layout === 'row') {
    return html`
      ${impersonating ? stopImpersonationButton('btn btn-warning btn-sm gap-1.5') : nothing}
      ${logoutButton('btn btn-ghost btn-sm gap-1.5')}
    `;
  }

  return html`
    <div class="flex flex-col gap-2">
      <div class="text-base-content/60 flex min-w-0 items-center gap-1.5 text-xs">
        <span class="icon-[material-symbols--account-circle-outline] shrink-0 text-base"></span>
        <span class="truncate" title=${account}>${account}</span>
      </div>
      ${impersonating
        ? html`<span class="badge badge-warning badge-sm gap-1 self-start">
            <span class="icon-[material-symbols--switch-account] text-sm"></span> Impersonating
          </span>`
        : nothing}
      ${impersonating ? stopImpersonationButton('btn btn-warning btn-sm w-full justify-start gap-1.5') : nothing}
      ${logoutButton('btn btn-ghost btn-sm w-full justify-start gap-1.5')}
    </div>
  `;
});
